import { DownloadIcon } from '@chakra-ui/icons';
import ScopePrompt from '../_shared/ScopePrompt';
import { Box, Button, Checkbox, HStack, Spinner, Text, useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from "react";
import { Calendar, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './courts-calendar.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ErrorState from '../../components/states/ErrorState';
import PageCard from '../../components/page-card';
import { translate, TranslationKeys } from '../../i18n';
import { CoachingSessionDto, CoachingSessionStatus } from '../../models/coach/dto/coaching-session.dto';
import { CourtAvailabilityDto } from '../../models/court/dto/court-availability.dto';
import { CourtTemporaryBlockadeDto } from '../../models/court/dto/court-temporary-blockade.dto';
import { GetCourtCalendarOutputDto } from '../../models/court/dto/get-court-calendar.dto';
import { SportType } from '../../models/court/dto/court.dto';
import { EventStatus } from '../../models/event/dto/event.dto';
import { GameDto, GameStatus, GameType } from '../../models/game/dto/game.dto';
import { useDateBoundary } from "../_shared/calendar/hooks/useDateBoundary";
import { courtBlockadeReasonEnumToString } from '../court-details/components/BlockadeReasonPicker';
import CalendarCheckboxShowAllDay from '../court-details/components/CalendarCheckboxShowAllDay';
import CalendarLegend, { CALENDAR_COLORS } from '../court-details/components/CalendarLegend';
import CalendarNavigation from '../court-details/components/CalendarNavigation';
import { useIsOrgManager } from '../auth/roles';
import CancelCourtDayModal from '../court-details/components/CancelCourtDayModal';
import CreateAdminBookingModal from '../court-details/components/CreateAdminBookingModal';
import CreateBlockadeOrCourtAvailabilityModal, { SelectedTimeSlotForBlockadeProps } from '../court-details/components/CreateBlockadeOrCourtAvailabilityModal';
import SelectedBlockadeModal from '../court-details/components/SelectedBlockadeModal';
import SelectedCourtAvailabilityModal from '../court-details/components/SelectedCourtAvailabilityModal';
import { calendarLocalizer, calendarMessages } from '../court-details/components/SharedCalendar';
import { CalendarEventType } from '../court-details/CourtDetailsPage';
import { GET_COURT_CALENDER_QUERY_KEY, useGetCourtsCalendar } from '../court-details/hooks/useGetCourtCalendar';
import { useGetCourts } from '../court-management/hooks/useGetCourts';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import WeatherStrip from '../weather/components/WeatherStrip';
import MultiCourtPicker from '../analytics/components/MultiCourtPicker';
import MultiSportPicker from './components/MultiSportPicker';
import { buildCalendarCsv, downloadCsv } from './exportCalendarToCsv';

export default function CourtsCalendarPage() {
    // Booking-for-a-player, availabilities, events and the rain-day cancel are
    // admin-only on the API; blockades are staff-level (admin or coach), so a
    // coach uses this page as a read view PLUS court blocking.
    const isOrgManager = useIsOrgManager();
    const [searchParams] = useSearchParams();
    const [dateBoundary, setDateBoundary] = useDateBoundary(1);
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError: isCourtsError, state: courtsReadState } = useGetCourts(selectedLocation?.id);
    const navigate = useNavigate();
    const toast = useToast();
    // Primary filter: sport(s). Empty array means "all sports".
    const [selectedSports, setSelectedSports] = useState<SportType[]>([]);
    // Secondary filter: court(s). Empty array means "all (sport-filtered) courts".
    const [selectedCourtIds, setSelectedCourtIds] = useState<number[]>([]);
    const availableSports = useMemo(() => {
        const set = new Set<SportType>();
        (courts ?? []).forEach(c => (c.sports ?? []).forEach(s => set.add(s)));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [courts]);
    // Courts matching the selected sports — drives the court picker's options
    // so the court filter adapts to the sport selection.
    const sportFilteredCourts = useMemo(() => {
        if (selectedSports.length === 0) return courts ?? [];
        return (courts ?? []).filter(c => c.sports?.some(s => selectedSports.includes(s)));
    }, [courts, selectedSports]);
    const filteredCourts = useMemo(() => {
        if (selectedCourtIds.length === 0) return sportFilteredCourts;
        return sportFilteredCourts.filter(c => selectedCourtIds.includes(c.id));
    }, [sportFilteredCourts, selectedCourtIds]);
    // When the sport selection changes, drop any picked courts that no longer
    // match so the court filter never references a hidden court.
    const handleSportsChange = useCallback((sports: SportType[]) => {
        setSelectedSports(sports);
        setSelectedCourtIds(prev => {
            if (prev.length === 0) return prev;
            const validIds = new Set(
                (courts ?? [])
                    .filter(c => sports.length === 0 || c.sports?.some(s => sports.includes(s)))
                    .map(c => c.id),
            );
            return prev.filter(id => validIds.has(id));
        });
    }, [courts]);
    const courtIds = useMemo(() => [...filteredCourts].sort((c1, c2) => c1.name.localeCompare(c2.name)).map(court => court.id), [filteredCourts]);
    const calendarQueries = useGetCourtsCalendar(courtIds, dateBoundary.start, dateBoundary.end);
    const isLoading = calendarQueries.some(q => q.isLoading);
    // A failed COURTS read leaves `courtIds` empty, so `calendarQueries` is an
    // empty array and both flags below were false: the page painted a calendar
    // with no bookings on it and said nothing. That is the same "a refused read
    // renders as an empty one" class as R89-4 — and here the operator reads the
    // empty grid as "the venue is free all day".
    const hasError = isCourtsError || calendarQueries.some(q => q.isError);
    const items = useMemo(() => calendarQueries.map(q => q.data), [calendarQueries]);

    const initialView = searchParams.get('view');
    const initialDate = searchParams.get('date');
    const [calendarViewType, setCalendarViewType] = useState<View>(
        initialView === 'day' || initialView === 'week' ? initialView : 'day'
    );
    const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(() => {
        if (!initialDate) return new Date();
        const parsed = new Date(initialDate);
        return isNaN(parsed.getTime()) ? new Date() : parsed;
    });
    const [showAllHours, setShowAllHours] = useState(false);
    const [mergeAvailabilities, setMergeAvailabilities] = useState(false);
    const [showCanceledEvents, setShowCanceledEvents] = useState(false);
    const [isCancelDayModalVisible, setIsCancelDayModalVisible] = useState(false);

    // MYS-45: rain hits the outdoor courts, so those are pre-ticked. When NO
    // court is flagged outdoor (the flag is optional and unset for most
    // tenants) the selection starts EMPTY rather than falling back to "all" —
    // pre-selecting every indoor court for a batch refund is not a safe default.
    const outdoorCourtIds = useMemo(
        () => filteredCourts.filter(c => c.isOutdoor).map(c => c.id),
        [filteredCourts],
    );

    const { events, backgroundEvents } = useMemo(() => {
        const nonNullArray = (items || []).filter(item => Boolean(item));
        const events: { start: Date; end: Date; title: string; resourceId: number; }[] = [];
        const backgroundEvents: { start: Date; end: Date; title: string; resourceId: number; }[] = [];
        nonNullArray.forEach(item => {
            // Filter games based on canceled status
            const filteredGames = showCanceledEvents
                ? item.games
                : item.games.filter(game => game.status !== GameStatus.CANCELED);

            // Filter coaching sessions based on canceled status
            const filteredCoachingSessions = showCanceledEvents
                ? item.coachingSessions
                : item.coachingSessions.filter(session => session.status !== CoachingSessionStatus.CANCELED);

            // Filter events based on canceled status
            const filteredEvents = showCanceledEvents
                ? (item.events || [])
                : (item.events || []).filter(event => event.status !== EventStatus.CANCELLED);

            events.push(...filteredGames.map(element => {
                const isCanceled = element.status === GameStatus.CANCELED;
                const titlePrefix = isCanceled ? '[CANCELED] ' : '';
                const orderIndicator = (element.orderCount ?? 0) > 0 ? ' 📦' : '';
                return {
                    title: `${titlePrefix}Match (${element.organiser?.firstName} ${element.organiser?.lastName})${orderIndicator}`,
                    gameId: element.id,
                    resourceId: item.court.id,
                    start: new Date(element.startTime),
                    end: new Date(new Date(element.startTime).getTime() + element.duration),
                    eventType: CalendarEventType.Game,
                    game: element
                };
            }));
            events.push(...filteredCoachingSessions.map(element => {
                const isCanceled = element.status === CoachingSessionStatus.CANCELED;
                const titlePrefix = isCanceled ? '[CANCELED] ' : '';
                return {
                    title: `${titlePrefix}Coaching (${element.user?.firstName} ${element.user?.lastName}) - [${element.court?.name}]`,
                    eventType: CalendarEventType.CoachingSession,
                    resourceId: item.court.id,
                    coachingSession: element,
                    start: new Date(element.startTime),
                    end: new Date(element.endTime),
                };
            }));
            events.push(...item.blockades.map(blockade => {
                return {
                    title: `Block - [${courtBlockadeReasonEnumToString(blockade.reason)}]${blockade.description ? ' - ' + blockade.description : ''}`,
                    eventType: CalendarEventType.Blockade,
                    resourceId: item.court.id,
                    start: new Date(blockade.startTime),
                    end: new Date(blockade.endTime),
                    blockade
                };
            }));
            events.push(...filteredEvents.map(event => {
                const isCanceled = event.status === EventStatus.CANCELLED;
                const titlePrefix = isCanceled ? '[CANCELED] ' : '';
                const courtsNames = event.courts?.map(c => c.name).join(', ') || '';
                return {
                    title: `${titlePrefix}Event: ${event.name}${courtsNames ? ` [${courtsNames}]` : ''}`,
                    eventType: CalendarEventType.Event,
                    resourceId: item.court.id,
                    start: new Date(event.startTime),
                    end: new Date(new Date(event.startTime).getTime() + event.duration),
                    event: event
                };
            }));

            // Process availabilities
            const availabilities = item.availabilities.map(availability => {
                let translationKey: TranslationKeys;
                if (availability.isCoachingAvailable && availability.isGameAvailable) {
                    translationKey = 'calendar.available_for_games_and_coaching';
                } else if (availability.isGameAvailable) {
                    translationKey = 'calendar.available_for_games';
                } else {
                    translationKey = 'calendar.available_for_coaching';
                }
                const title = translate(translationKey);

                return {
                    availability,
                    title,
                    resourceId: item.court.id,
                    start: new Date(availability.startTime),
                    end: new Date(availability.endTime),
                }
            });

            if (mergeAvailabilities) {
                // Merge overlapping availabilities for the same court
                const mergedAvailabilities = availabilities.reduce((acc, curr) => {
                    const existing = acc.find(a =>
                        a.resourceId === curr.resourceId &&
                        a.start <= curr.end &&
                        a.end >= curr.start
                    );

                    if (existing) {
                        // Merge with existing availability
                        existing.start = new Date(Math.min(existing.start.getTime(), curr.start.getTime()));
                        existing.end = new Date(Math.max(existing.end.getTime(), curr.end.getTime()));
                        return acc;
                    } else {
                        // Add new availability
                        return [...acc, curr];
                    }
                }, [] as typeof availabilities);

                backgroundEvents.push(...mergedAvailabilities);
            } else {
                backgroundEvents.push(...availabilities);
            }
        });
        return { events, backgroundEvents };
    }, [items, mergeAvailabilities, showCanceledEvents]);

    const resources = useMemo(() => {
        return (items || []).filter(Boolean).map(item => ({
            resourceId: item.court.id,
            resourceTitle: item.court.name
        }));
    }, [items]);

    const courtIdToName = useMemo(() => {
        const map = new Map<number, string>();
        (items || []).filter(Boolean).forEach((item) => {
            if (item?.court) map.set(item.court.id, item.court.name);
        });
        return map;
    }, [items]);

    const handleExportCalendar = () => {
        if (!events.length) {
            toast({
                title: translate('calendar.export_empty'),
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        const getCourtName = (courtId: number) => courtIdToName.get(courtId) ?? String(courtId);
        const csv = buildCalendarCsv(
            events as any,
            getCourtName,
            (key: string) => String(translate(key as TranslationKeys) ?? key)
        );
        const filename = `calendar_${format(dateBoundary.start, 'yyyy-MM-dd')}_${format(dateBoundary.end, 'yyyy-MM-dd')}`;
        downloadCsv(csv, filename);
        toast({
            title: translate('calendar.export_success'),
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const minDate = useMemo(() => {
        if (showAllHours) return new Date(1970, 4, 1, 0, 0, 0, 0);
        if (!events.length && !backgroundEvents.length) return null;
        const minHour = Math.min(...events.map(e => e.start.getHours()), ...backgroundEvents.map(e => e.start.getHours()));
        return new Date(1970, 4, 1, minHour, 0, 0, 0);
    }, [events, backgroundEvents, showAllHours]);

    const [selectedBlockade, setSelectedBlockade] = useState<CourtTemporaryBlockadeDto>();
    const [isBlockadeModalVisible, setIsBlockadeModalVisible] = useState(false);
    const [selectedCourtAvailability, setSelectedCourtAvailability] = useState<CourtAvailabilityDto>();
    const [isCourtAvailabilityModalVisible, setIsCourtAvailabilityModalVisible] = useState(false);

    const [courtId, setCourtId] = useState<number>();
    // Timeslot for creation
    const [isCreatingBlockadeModalVisible, setIsCreatingBlockadeModalVisible] = useState(false);
    const [selectedTimeSlot, setSelectedTimeslot] = useState<SelectedTimeSlotForBlockadeProps>();
    const [isAdminBookingModalVisible, setIsAdminBookingModalVisible] = useState(false);
    // Prefill state for the admin-booking modal when launched from a
    // calendar drag (rather than the standalone button up top). Cleared
    // every time the modal closes so a subsequent click from the button
    // doesn't inherit a stale timeslot from the previous drag.
    const [adminBookingPrefill, setAdminBookingPrefill] = useState<{
        startTime: Date;
        durationMinutes: number;
        courtId: number;
    } | null>(null);
    // Scope admin booking (user search + booking creation) to the org that
    // owns the courts currently displayed, i.e. the selected location's org —
    // NOT the logged-in admin's home org. A superadmin can switch orgs via the
    // picker, which drives selectedLocation; using useGetCurrentOrganization()
    // here pinned bookings to the admin's home org and made the player search
    // return nothing whenever they were viewing another org's calendar.
    const currentOrganizationId = selectedLocation?.organizationId;
    // No role gate here: court blockades are staff-level on the API
    // (`hasOrganizationStaffAccess` — admin OR coach), so a coach dragging a
    // slot gets the creation modal too. The modal itself hides the
    // admin-only modes (availability, event, booking) for a coach.
    const onTimeSlotSelected = useCallback((start: Date, end: Date, resourceCourtId?: number) => {
        const effectiveCourtId = resourceCourtId ?? courtId;
        if (effectiveCourtId != null) setCourtId(effectiveCourtId);
        setSelectedTimeslot({
            startTime: start,
            endTime: end,
            courtId: effectiveCourtId
        })
        setIsCreatingBlockadeModalVisible(true);
    }, [courtId]);

    const queryClient = useQueryClient();
    const onBlockadeCreated = (blockade: CourtTemporaryBlockadeDto) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, blockades: [...newData.blockades, blockade] };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onAvailabilitiesCreated = (items: CourtAvailabilityDto[]) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, availabilities: [...newData.availabilities, ...items] };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onBlockadeDeleted = (blockadeId: string) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, blockades: newData.blockades.filter(blockade => blockade.id !== blockadeId) };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onCourtAvailabilityDeleted = (availabilityId: string) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, availabilities: newData.availabilities.filter(availability => availability.id !== availabilityId) };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };

    const onRangeChange = useCallback((start: Date, end: Date) => {
        setDateBoundary({ start, end });
    }, [setDateBoundary]);

    const onEventSelected = (event: any) => {
        setCourtId(event.resourceId);
        const hasBlockade = Boolean(event.blockade);
        const hasAvailability = Boolean(event.availability);
        if (hasBlockade) {
            setSelectedBlockade(event.blockade);
            setIsBlockadeModalVisible(true);
        } else if (hasAvailability) {
            setSelectedCourtAvailability(event.availability);
            setIsCourtAvailabilityModalVisible(true);
        }
    };

    return (
        <PageCard
            title={translate('tabs.calendar') || 'Calendrier des terrains'}
            rightHeader={
                <HStack spacing={2}>
                    {isOrgManager && (
                    <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => setIsAdminBookingModalVisible(true)}
                        isDisabled={!currentOrganizationId || !(courts ?? []).length}
                    >
                        Réserver pour un joueur
                    </Button>
                    )}
                    {isOrgManager && (
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => setIsCancelDayModalVisible(true)}
                        // Gated on the FILTERED courts — that is what the modal
                        // receives. Gating on all courts left the button live
                        // with a sport filter that matched nothing.
                        isDisabled={!filteredCourts.length}
                    >
                        {translate('court.cancel_day')}
                    </Button>
                    )}
                    <Button
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        onClick={handleExportCalendar}
                        isDisabled={!events.length}
                    >
                        {translate('calendar.export_to_csv') || 'Export to CSV'}
                    </Button>
                </HStack>
            }>
            <CreateBlockadeOrCourtAvailabilityModal
                selectedTimeslot={selectedTimeSlot}
                courtId={courtId}
                courts={courts ?? []}
                isOrgManager={isOrgManager}
                onCourtAvailabilitiesAdded={onAvailabilitiesCreated}
                isModalVisible={isCreatingBlockadeModalVisible}
                setIsModalVisible={setIsCreatingBlockadeModalVisible}
                onBlockadeCreated={onBlockadeCreated}
                onEventCreated={() => queryClient.invalidateQueries({ queryKey: ['courtCalendar'] })}
                // Only surface the admin-booking option when the org
                // context is loaded — without organizationId the
                // CreateAdminBookingModal would fail at the network call.
                onCreateAdminBooking={
                    currentOrganizationId
                        ? (timeslot) => {
                            const durationMinutes = Math.max(
                                Math.round(
                                    (timeslot.endTime.getTime() -
                                        timeslot.startTime.getTime()) /
                                        60_000,
                                ),
                                15,
                            );
                            setAdminBookingPrefill({
                                startTime: timeslot.startTime,
                                durationMinutes,
                                courtId: timeslot.courtId,
                            });
                            setIsAdminBookingModalVisible(true);
                        }
                        : undefined
                }
            />
            {/* Managers only, and again the gate belongs on the MOUNT: this
                modal reads the org's capacity options at mount, so a coach —
                who has no way to open it, both entry points being role-gated —
                was firing that request on every visit to their landing page. */}
            {isOrgManager && currentOrganizationId && (courts ?? []).length > 0 && (
                <CreateAdminBookingModal
                    isOpen={isAdminBookingModalVisible}
                    onClose={() => {
                        setIsAdminBookingModalVisible(false);
                        // Drop the prefill on close so the next standalone-
                        // button click opens the modal with the regular
                        // "now + 1h" defaults.
                        setAdminBookingPrefill(null);
                    }}
                    organizationId={currentOrganizationId}
                    courts={courts ?? []}
                    initialCourtId={adminBookingPrefill?.courtId}
                    initialStartTime={adminBookingPrefill?.startTime}
                    initialDurationMinutes={adminBookingPrefill?.durationMinutes}
                    onCreated={() =>
                        queryClient.invalidateQueries({ queryKey: ['courtCalendar'] })
                    }
                />
            )}
            {/* Mounted only while open: the modal fetches a full-day calendar
                per selected court for its preview, and mounting it
                unconditionally fired those requests on every visit to this
                page for a modal nobody had opened — the same mount-cost trap
                already documented on CreateAdminBookingModal below. */}
            {isCancelDayModalVisible && (
                <CancelCourtDayModal
                    isOpen
                    onClose={() => setIsCancelDayModalVisible(false)}
                    courts={filteredCourts}
                    initialCourtIds={outdoorCourtIds}
                    initialDate={currentCalendarDate}
                />
            )}
            <SelectedBlockadeModal
                setIsModalVisible={setIsBlockadeModalVisible}
                isModalVisible={isBlockadeModalVisible}
                onBlockadeDeleted={onBlockadeDeleted}
                selectedBlockade={selectedBlockade}
                organizationId={currentOrganizationId}
            />
            <SelectedCourtAvailabilityModal
                isModalVisible={isCourtAvailabilityModalVisible}
                setIsModalVisible={setIsCourtAvailabilityModalVisible}
                selectedAvailability={selectedCourtAvailability}
                onCourtAvailabilityDeleted={onCourtAvailabilityDeleted}
            />
            <Box p={4} >
                {/* Calendar Navigation */}
                <CalendarNavigation
                    onViewChange={(view) => setCalendarViewType(view)}
                    onDateBoundsChange={(start, end) => onRangeChange(start, end)}
                    onCurrentDateChange={(date) => setCurrentCalendarDate(date)}
                    initialView={(calendarViewType === 'day' || calendarViewType === 'week') ? calendarViewType : 'day'}
                />

                <HStack spacing={3} mb={4} flexWrap="wrap" fontSize="sm">
                    <MultiSportPicker
                        sports={availableSports}
                        selectedSports={selectedSports}
                        onChange={handleSportsChange}
                    />
                    <MultiCourtPicker
                        courts={sportFilteredCourts}
                        selectedCourtIds={selectedCourtIds}
                        onChange={setSelectedCourtIds}
                    />
                    <CalendarCheckboxShowAllDay selected={showAllHours} setSelected={setShowAllHours} />
                    <Checkbox
                        size="sm"
                        isChecked={mergeAvailabilities}
                        onChange={(e) => setMergeAvailabilities(e.target.checked)}
                    >
                        <Text fontSize="sm">{translate('calendar.merge_availabilities')}</Text>
                    </Checkbox>
                    <Checkbox
                        size="sm"
                        isChecked={showCanceledEvents}
                        onChange={(e) => setShowCanceledEvents(e.target.checked)}
                    >
                        <Text fontSize="sm">{translate('calendar.show_canceled_events')}</Text>
                    </Checkbox>
                </HStack>
                <CalendarLegend />
                {isLoading && (
                    <HStack justify="center" py={4}>
                        <Spinner size="sm" />
                        <Text fontSize="sm">{translate('calendar.loading')}</Text>
                    </HStack>
                )}
                {hasError && !isLoading && (
                    <ErrorState
                        message={translate(
                            isCourtsError
                                ? 'courts.error_loading'
                                : 'calendar.error_loading',
                        )}
                    />
                )}
                <WeatherStrip
                    locationId={selectedLocation?.id}
                    date={currentCalendarDate}
                    hasOutdoorCourts={(filteredCourts ?? []).some(c => c.isOutdoor)}
                />
                {/*
                    A day grid with no court columns is not a neutral blank: it
                    is the calendar saying the venue has nothing on. Before run
                    134 gated `useGetCourts` on `locationId`, an operator with no
                    club selected got exactly that — the read went out as
                    `courts?locationId=undefined`, answered 200 with `[]`, and
                    `resources` was empty with no error and no spinner anywhere
                    (R110-6 / R133-4, D58's empty-grid shape).
                */}
                {courtsReadState === 'never-issued' ? (
                    <ScopePrompt gate="location" />
                ) : (
                <Box className="courts-calendar" data-tour="coach-planning">
                    <Calendar
                        resources={resources}
                        min={minDate}
                        messages={calendarMessages}
                        resourceIdAccessor="resourceId"
                        resourceTitleAccessor="resourceTitle"
                        localizer={calendarLocalizer}
                        events={events}
                        backgroundEvents={backgroundEvents}
                        toolbar={false}
                        culture='fr'
                        endAccessor="end"
                        selectable={true}
                        showAllEvents={false}
                        date={currentCalendarDate}
                        view={calendarViewType}
                        views={['day', 'week']}

                        onSelectSlot={(info) => {
                            if (info.action !== 'select') return;
                            const slotCourtId = info.resourceId ? Number.parseInt(info.resourceId.toString()) : undefined;
                            const { start, end } = info;
                            onTimeSlotSelected(start, end, slotCourtId);
                        }}

                        onSelectEvent={(e) => {
                            const gameId = (e as any).gameId;
                            const coachingSession = (e as any).coachingSession;
                            const eventData = (e as any).event;
                            if (gameId) {
                                navigate(`/admin/gameDetails?game=${gameId}`);
                            } else if (coachingSession) {
                                navigate(`/admin/coachingSessionDetails?coachingSession=${coachingSession.id}`);
                            } else if (eventData) {
                                navigate(`/admin/eventDetails?event=${eventData.id}`);
                            }
                            onEventSelected(e);
                        }}
                        onRangeChange={() => {
                            // Disabled — navigation handled by CalendarNavigation
                        }}

                        eventPropGetter={calendarEventPropGetter}
                    />
                </Box>
                )}
            </Box>
        </PageCard>
    );
}

const CANCELED_STYLE: React.CSSProperties = {
    backgroundColor: '#B8453A',
    opacity: 0.5,
    textDecoration: 'line-through',
    border: '1px dashed #96362C',
    color: '#FFFFFF',
};

export function calendarEventPropGetter(event: any, start: Date, end: Date, isSelected: boolean) {
    const eventType: CalendarEventType = event.eventType;
    const game = event.game as GameDto | undefined;
    const coachingSession = event.coachingSession as CoachingSessionDto | undefined;
    const eventData = event.event;
    const isFullyPaid = Boolean(game) && (game.type === GameType.PRIVATE || game.participants.length === game.capacity);

    const isGameCanceled = Boolean(game) && game.status === GameStatus.CANCELED;
    const isCoachingSessionCanceled = Boolean(coachingSession) && coachingSession.status === CoachingSessionStatus.CANCELED;
    const isEventCanceled = Boolean(eventData) && eventData.status === EventStatus.CANCELLED;

    let style: React.CSSProperties = { color: '#FFFFFF' };

    if (eventType === CalendarEventType.Blockade) {
        style = { ...style, backgroundColor: CALENDAR_COLORS.BLOCKADE };
    } else if (eventType === CalendarEventType.Event) {
        style = isEventCanceled ? { ...CANCELED_STYLE } : { ...style, backgroundColor: CALENDAR_COLORS.EVENT };
    } else if (eventType === CalendarEventType.CoachingSession) {
        style = isCoachingSessionCanceled ? { ...CANCELED_STYLE } : { ...style, backgroundColor: CALENDAR_COLORS.COACHING_SESSION };
    } else if (isGameCanceled) {
        style = { ...CANCELED_STYLE };
    } else {
        const color = isFullyPaid ? CALENDAR_COLORS.MATCH_FULLY_BOOKED : CALENDAR_COLORS.MATCH_PARTIALLY_BOOKED;
        style = { ...style, backgroundColor: color };
    }

    // Order indicator: warm amber left border when the game has orders
    const hasOrders = Boolean(game) && (game as any).orderCount > 0;
    if (hasOrders) {
        style = { ...style, borderLeft: `4px solid ${CALENDAR_COLORS.ORDERS}` };
    }

    return { style };
}